import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Camera, Users } from "lucide-react";

const PLACEHOLDERS = [
  { id: "p2", title: "Under Angels Landing", location: "Springdale, UT", lat: 37.20, lng: -112.99, status: "Pre-Production" },
  { id: "p3", title: "The Escalante", location: "Escalante, UT", lat: 37.77, lng: -111.60, status: "Post" },
  { id: "p4", title: "Ridge / Ridge", location: "Moab, UT", lat: 38.57, lng: -109.55, status: "Shooting" },
  { id: "p5", title: "Silver Reef", location: "Leeds, UT", lat: 37.23, lng: -113.36, status: "Development" },
];

// Southern Utah approximate bounds: lat 36.9–38.9, lng -114.05 – -109.05
const BOUNDS = { minLat: 36.9, maxLat: 38.9, minLng: -114.1, maxLng: -109.0 };

function project(lat, lng, w, h) {
  const x = ((lng - BOUNDS.minLng) / (BOUNDS.maxLng - BOUNDS.minLng)) * w;
  const y = h - ((lat - BOUNDS.minLat) / (BOUNDS.maxLat - BOUNDS.minLat)) * h;
  return { x, y };
}

export default function Productions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.productions();
        const arr = Array.isArray(data) && data.length ? data : PLACEHOLDERS;
        if (alive) { setItems(arr); setSelected(arr[0]); }
      } catch { if (alive) { setItems(PLACEHOLDERS); setSelected(PLACEHOLDERS[0]); } }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">Production Map</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none font-medium">Who's shooting where.</h1>
        <p className="text-neutral-400 mt-3 max-w-xl">A live look across Southern Utah — Zion, Kanab, Moab, and everything in between.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="ff-card p-4 relative overflow-hidden" data-testid="productions-map">
          <div className="relative w-full h-[520px] rounded-xl overflow-hidden bg-[#0a0908]" style={{ backgroundImage: "radial-gradient(circle at 15% 30%, rgba(255,90,0,0.06), transparent 40%), radial-gradient(circle at 80% 70%, rgba(211,47,47,0.06), transparent 40%)" }}>
            <svg viewBox="0 0 800 520" className="absolute inset-0 w-full h-full">
              {/* subtle grid */}
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                </pattern>
              </defs>
              <rect width="800" height="520" fill="url(#grid)" />
              {/* Utah state outline (approximate) */}
              <path d="M 60 60 L 740 60 L 740 200 L 720 200 L 720 470 L 60 470 Z"
                    fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.10)" strokeWidth="1.5" />
              {/* Pins */}
              {items.map((p, i) => {
                const { x, y } = project(p.lat || 37.5, p.lng || -112.5, 800, 520);
                const active = selected?.id === p.id;
                return (
                  <g key={p.id || i} onClick={() => setSelected(p)} style={{ cursor: "pointer" }}>
                    <circle cx={x} cy={y} r={active ? 16 : 10} fill="rgba(255,90,0,0.15)" />
                    <circle cx={x} cy={y} r={active ? 8 : 6} fill={active ? "#FF5A00" : "#FF5A00"} stroke="#050505" strokeWidth="2" />
                    <text x={x + 14} y={y + 4} fontSize="12" fill="#e5e5e5" fontFamily="Outfit">{p.title}</text>
                  </g>
                );
              })}
            </svg>
            <div className="absolute bottom-4 left-4 text-[10px] uppercase tracking-widest text-neutral-500">
              Southern Utah · Kanab · Zion · Moab · Escalante
            </div>
          </div>
        </div>
        <aside className="space-y-4">
          <div className="ff-card p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Selected production</div>
            {selected ? (
              <>
                <div className="font-display text-2xl tracking-tight">{selected.title}</div>
                <div className="text-sm text-neutral-400 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5 ff-accent-text" /> {selected.location}</div>
                <div className="mt-3 text-xs uppercase tracking-widest text-neutral-500">Status</div>
                <div className="text-sm">{selected.status || "Active"}</div>
                <div className="mt-4 flex items-center gap-4 text-xs text-neutral-500">
                  <span className="inline-flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Crew</span>
                  <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Cast</span>
                </div>
              </>
            ) : (
              <div className="text-sm text-neutral-500">Click a pin to see details.</div>
            )}
          </div>
          <div className="ff-card p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">All productions</div>
            {loading ? (
              <div className="text-sm text-neutral-500">Loading…</div>
            ) : (
              <ul className="space-y-2 text-sm">
                {items.map((p) => (
                  <li key={p.id}>
                    <button onClick={() => setSelected(p)} className={`w-full text-left px-3 py-2 rounded-md transition-colors ${selected?.id === p.id ? "bg-white/10 ff-accent-text" : "hover:bg-white/5 text-neutral-300"}`}>
                      <div className="font-medium">{p.title}</div>
                      <div className="text-xs text-neutral-500">{p.location} · {p.status}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
